import { Head, Link, usePage } from "@inertiajs/react"
import { createConsumer } from "@rails/actioncable"
import { type ReactElement, type ReactNode, useEffect, useState } from "react"

import HeadingSmall from "@/components/heading-small"
import PlatformIcon from "@/components/icons/platform-icon"
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card } from "@/components/ui/card"
import {
  HoverCard,
  HoverCardContent,
  HoverCardTrigger,
} from "@/components/ui/hover-card"
import { Input } from "@/components/ui/input"
import AppLayout from "@/layouts/app-layout"
import {
  authorizeConnectedAccountsPath,
  connectedAccountsGithubConfigurePath,
  connectedAccountsPath,
  connectedAccountsPinterestRefreshPath,
  disconnectConnectedAccountsPath,
} from "@/routes"
import type {
  BreadcrumbItem,
  PinterestBoard,
  Platform,
  PlatformCredential,
  PlatformCredentials,
  SharedProps,
} from "@/types"

interface Props {
  platform_credentials: PlatformCredentials
}

const breadcrumbs: BreadcrumbItem[] = [
  { title: "Connected Accounts", href: connectedAccountsPath() },
]

function AccountAvatar({ avatarUrl }: { avatarUrl: string | null }) {
  if (!avatarUrl) return null

  return (
    <Avatar className="size-6">
      <AvatarImage src={avatarUrl} alt="" />
      <AvatarFallback />
    </Avatar>
  )
}

function AccountCard({
  platform,
  identity,
  children,
  actions,
}: {
  platform: Platform
  identity?: ReactNode
  children?: ReactNode
  actions: ReactNode
}) {
  return (
    <Card className="flex h-full flex-col gap-4 p-4">
      <div className="flex items-center gap-3">
        <PlatformIcon platform={platform.name} className="size-8 shrink-0" />
        <span className="sr-only">{platform.label}</span>
        {identity}
        {/* Early-access platforms are still awaiting platform-side API approval
            and aren't fully working yet, so we badge them. The flag lives on the
            platform object; gated platforms only reach this page when the team
            has early access. */}
        {platform.early_access && (
          <Badge variant="secondary" className="ml-auto shrink-0">
            Early access
          </Badge>
        )}
      </div>
      {children && <div className="text-sm">{children}</div>}
      <div className="mt-auto flex flex-wrap justify-end gap-2">{actions}</div>
    </Card>
  )
}

function AccountIdentity({ credential }: { credential: PlatformCredential }) {
  if (!credential.user_identifier) return null

  return (
    <p className="flex min-w-0 items-center gap-2 text-sm">
      <AccountAvatar avatarUrl={credential.avatar_url} />
      <span className="truncate">{credential.user_identifier}</span>
    </p>
  )
}

function AccountCardWithCredential({
  platform,
  credential,
  addAnother = false,
}: {
  platform: Platform
  credential: PlatformCredential | null
  addAnother?: boolean
}) {
  return (
    <AccountCard
      platform={platform}
      identity={credential && <AccountIdentity credential={credential} />}
      actions={
        credential ? (
          <>
            {credential.expired && (
              <Button size="sm" asChild>
                <a href={authorizeConnectedAccountsPath(platform.name)}>
                  Reconnect
                </a>
              </Button>
            )}
            <Button variant="destructive" size="sm" asChild>
              <Link
                method="delete"
                href={disconnectConnectedAccountsPath(credential.id)}
                as="button"
              >
                Disconnect
              </Link>
            </Button>
          </>
        ) : (
          <Button size="sm" asChild>
            <a href={authorizeConnectedAccountsPath(platform.name)}>
              {addAnother ? "Connect another account" : "Connect"}
            </a>
          </Button>
        )
      }
    >
      {credential?.expired && (
        <Badge variant="destructive">
          Connection expired — reconnect to keep publishing
        </Badge>
      )}
      {!credential && !addAnother && (
        <p className="text-muted-foreground">Not connected</p>
      )}
    </AccountCard>
  )
}

function MastodonAccountCard({
  platform,
  credential,
  addAnother = false,
}: {
  platform: Platform
  credential: PlatformCredential | null
  addAnother?: boolean
}) {
  const [showForm, setShowForm] = useState(false)
  const [instance, setInstance] = useState("")

  if (credential) {
    const identityLabel = [
      credential.user_identifier,
      credential.mastodon_instance_url,
    ]
      .filter(Boolean)
      .join(" · ")

    return (
      <AccountCard
        platform={platform}
        identity={
          identityLabel && (
            <p className="flex min-w-0 items-center gap-2 text-sm">
              <AccountAvatar avatarUrl={credential.avatar_url} />
              <span className="truncate">{identityLabel}</span>
            </p>
          )
        }
        actions={
          <Button variant="destructive" size="sm" asChild>
            <Link
              method="delete"
              href={disconnectConnectedAccountsPath(credential.id)}
              as="button"
            >
              Disconnect
            </Link>
          </Button>
        }
      />
    )
  }

  return (
    <AccountCard
      platform={platform}
      actions={
        !showForm && (
          <Button size="sm" onClick={() => setShowForm(true)}>
            {addAnother ? "Connect another account" : "Connect"}
          </Button>
        )
      }
    >
      {!showForm && !addAnother && (
        <p className="text-muted-foreground">Not connected</p>
      )}
      {showForm && (
        <form
          className="flex flex-col gap-2"
          action={authorizeConnectedAccountsPath(platform.name)}
          method="get"
        >
          <Input
            name="mastodon_instance"
            placeholder="mastodon.social"
            value={instance}
            onChange={(e) => setInstance(e.target.value)}
            className="w-full"
          />
          <div className="flex items-center gap-2">
            <Button type="submit" size="sm">
              Authorize
            </Button>
            <Button
              type="button"
              size="sm"
              variant="ghost"
              onClick={() => setShowForm(false)}
            >
              Cancel
            </Button>
          </div>
        </form>
      )}
    </AccountCard>
  )
}

function PinterestAccountCard({
  platform,
  credential,
  addAnother = false,
}: {
  platform: Platform
  credential: PlatformCredential | null
  addAnother?: boolean
}) {
  const [boards, setBoards] = useState<PinterestBoard[]>(
    credential?.pinterest_boards ?? [],
  )
  const credentialId = credential?.id

  useEffect(() => {
    if (credentialId == null) return

    const consumer = createConsumer()
    const subscription = consumer.subscriptions.create(
      { channel: "Pinterest::BoardsChannel", credential_id: credentialId },
      {
        received(data: { boards: PinterestBoard[] }) {
          setBoards(data.boards)
        },
      },
    )
    return () => {
      subscription.unsubscribe()
      consumer.disconnect()
    }
  }, [credentialId])

  if (!credential) {
    return (
      <AccountCardWithCredential
        platform={platform}
        credential={null}
        addAnother={addAnother}
      />
    )
  }

  const boardCount = `${boards.length} ${boards.length === 1 ? "board" : "boards"}`

  const boardCountNode =
    boards.length > 0 ? (
      <HoverCard openDelay={100}>
        <HoverCardTrigger asChild>
          <span className="cursor-help underline decoration-dotted underline-offset-2">
            {boardCount}
          </span>
        </HoverCardTrigger>
        <HoverCardContent className="max-h-72 w-72 overflow-y-auto">
          <p className="mb-2 text-xs font-semibold">Boards</p>
          <ul className="space-y-1 text-sm">
            {boards.map((board) => (
              <li key={board.id}>{board.name}</li>
            ))}
          </ul>
        </HoverCardContent>
      </HoverCard>
    ) : (
      <span>{boardCount}</span>
    )

  return (
    <AccountCard
      platform={platform}
      identity={<AccountIdentity credential={credential} />}
      actions={
        <>
          <Button variant="outline" size="sm" asChild>
            <Link
              method="post"
              href={connectedAccountsPinterestRefreshPath(credential.id)}
              as="button"
              preserveState
              preserveScroll
            >
              Refresh boards
            </Link>
          </Button>
          <Button variant="destructive" size="sm" asChild>
            <Link
              method="delete"
              href={disconnectConnectedAccountsPath(credential.id)}
              as="button"
            >
              Disconnect
            </Link>
          </Button>
        </>
      }
    >
      <p className="text-muted-foreground">{boardCountNode}</p>
    </AccountCard>
  )
}

function GithubAccountCard({
  platform,
  credential,
  addAnother = false,
}: {
  platform: Platform
  credential: PlatformCredential | null
  addAnother?: boolean
}) {
  if (credential) {
    return (
      <AccountCard
        platform={platform}
        identity={
          credential.github_repo && (
            <p className="flex min-w-0 items-center gap-2 text-sm">
              <AccountAvatar avatarUrl={credential.avatar_url} />
              <span className="truncate">{credential.github_repo}</span>
            </p>
          )
        }
        actions={
          <>
            <Button variant="outline" size="sm" asChild>
              <Link
                href={connectedAccountsGithubConfigurePath({
                  credential_id: credential.id,
                })}
              >
                Configure
              </Link>
            </Button>
            <Button variant="destructive" size="sm" asChild>
              <Link
                method="delete"
                href={disconnectConnectedAccountsPath(credential.id)}
                as="button"
              >
                Disconnect
              </Link>
            </Button>
          </>
        }
      >
        <p className="text-muted-foreground">
          Publish posts to a Jekyll site via GitHub.
        </p>
      </AccountCard>
    )
  }

  return (
    <AccountCard
      platform={platform}
      actions={
        <Button size="sm" asChild>
          <a href={authorizeConnectedAccountsPath(platform.name)}>
            {addAnother ? "Connect another site" : "Connect"}
          </a>
        </Button>
      }
    >
      <div className="space-y-1">
        {!addAnother && <p className="text-muted-foreground">Not connected</p>}
        <p className="text-muted-foreground">
          Publish posts to a Jekyll site via GitHub.
        </p>
      </div>
    </AccountCard>
  )
}

// A handful of platforms need a bespoke card; the rest share the generic one.
// This is component dispatch, not a data lookup — every platform's attributes
// (early access, label, etc.) are read straight off its Platform object.
function renderAccountCard(
  platform: Platform,
  credential: PlatformCredential | null,
  addAnother = false,
): ReactElement {
  switch (platform.name) {
    case "pinterest":
      return (
        <PinterestAccountCard
          platform={platform}
          credential={credential}
          addAnother={addAnother}
        />
      )
    case "mastodon":
      return (
        <MastodonAccountCard
          platform={platform}
          credential={credential}
          addAnother={addAnother}
        />
      )
    case "github":
      return (
        <GithubAccountCard
          platform={platform}
          credential={credential}
          addAnother={addAnother}
        />
      )
    default:
      return (
        <AccountCardWithCredential
          platform={platform}
          credential={credential}
          addAnother={addAnother}
        />
      )
  }
}

export default function ConnectedAccountsShow({ platform_credentials }: Props) {
  // `platforms` is already the set the team can use (team-filtered by
  // Platform.visible_to and ordered by display_order). Development-only platforms
  // (e.g. the always_fail/sleep test stubs) are surfaced by the shared prop in
  // development but aren't real connectable accounts, so skip them. A team can now
  // connect multiple accounts per platform, so we render one card per credential
  // plus a "connect another account" card for every connectable platform.
  const { platforms } = usePage<SharedProps>().props

  const entries = platforms
    .filter((platform) => !platform.development_only)
    .map((platform) => ({
      platform,
      credentials: platform_credentials.filter(
        (credential) => credential.platform_name === platform.name,
      ),
    }))

  const connectedCards = entries.flatMap(({ platform, credentials }) =>
    credentials.map((credential) => ({
      key: `${platform.name}-${credential.id}`,
      node: renderAccountCard(platform, credential),
    })),
  )

  return (
    <AppLayout breadcrumbs={breadcrumbs}>
      <Head title="Connected Accounts" />

      <div className="space-y-6 px-4 py-6">
        <HeadingSmall
          title="Connected Accounts"
          description="Connect your social media accounts to publish posts"
        />

        {connectedCards.length > 0 && (
          <section className="space-y-3">
            <h3 className="text-sm font-semibold">Connected</h3>
            <div className="grid auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {connectedCards.map(({ key, node }) => (
                <div key={key}>{node}</div>
              ))}
            </div>
          </section>
        )}

        <section className="space-y-3">
          <h3 className="text-sm font-semibold">Connect an account</h3>
          <div className="grid auto-rows-fr grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
            {entries.map(({ platform, credentials }) => (
              <div key={platform.name}>
                {renderAccountCard(platform, null, credentials.length > 0)}
              </div>
            ))}
          </div>
        </section>
      </div>
    </AppLayout>
  )
}
